/**
 * Form validation utilities
 * Derek-friendly: clear, simple error messages
 */

export interface ValidationResult {
  isValid: boolean
  error?: string
}

/**
 * Validate email format
 */
export function validateEmail(email: string): ValidationResult {
  if (!email || !email.trim()) {
    return { isValid: false, error: 'Email is required.' }
  }

  const trimmed = email.trim()

  // Basic email format check
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!emailRegex.test(trimmed)) {
    return { isValid: false, error: 'Please enter a valid email address.' }
  }

  // Check for common issues
  if (trimmed.length > 254) {
    return { isValid: false, error: 'Email address is too long.' }
  }

  return { isValid: true }
}

/**
 * Validate name (optional field, but validate if provided)
 */
export function validateName(name: string): ValidationResult {
  if (!name || !name.trim()) {
    return { isValid: true } // Name is optional
  }

  const trimmed = name.trim()

  if (trimmed.length < 2) {
    return { isValid: false, error: 'Name must be at least 2 characters.' }
  }

  if (trimmed.length > 100) {
    return { isValid: false, error: 'Name must be 100 characters or less.' }
  }

  // Check for obviously invalid characters (allowing unicode names)
  if (/[<>{}[\]\\]/.test(trimmed)) {
    return { isValid: false, error: 'Name contains invalid characters.' }
  }

  return { isValid: true }
}

/**
 * Validate company name (optional field)
 */
export function validateCompany(company: string): ValidationResult {
  if (!company || !company.trim()) {
    return { isValid: true } // Company is optional
  }

  const trimmed = company.trim()

  if (trimmed.length > 200) {
    return { isValid: false, error: 'Company name must be 200 characters or less.' }
  }

  // Check for obviously invalid characters
  if (/[<>{}[\]\\]/.test(trimmed)) {
    return { isValid: false, error: 'Company name contains invalid characters.' }
  }

  return { isValid: true }
}

/**
 * Validate required field
 */
export function validateRequired(
  value: string,
  fieldName: string
): ValidationResult {
  if (!value || !value.trim()) {
    return { isValid: false, error: `${fieldName} is required.` }
  }
  return { isValid: true }
}

/**
 * Validate string length
 */
export function validateLength(
  value: string,
  minLength: number,
  maxLength: number,
  fieldName: string
): ValidationResult {
  const trimmed = (value || '').trim()

  if (trimmed.length < minLength) {
    return {
      isValid: false,
      error: `${fieldName} must be at least ${minLength} characters.`,
    }
  }

  if (trimmed.length > maxLength) {
    return {
      isValid: false,
      error: `${fieldName} must be ${maxLength} characters or less.`,
    }
  }

  return { isValid: true }
}

/**
 * Helper to run validation only on blur/submit (not while typing)
 */
export function shouldShowError(
  touched: boolean,
  submitted: boolean,
  error?: string
): string | undefined {
  if (!error) return undefined
  if (touched || submitted) return error
  return undefined
}
